import { jsPDF } from 'jspdf';
import { clean, safeName } from './exporters';
import { APP_INFO } from './license';
import { resolvePt } from './model';

const fx = (project) => Object.fromEntries((project.fittings || []).map((f) => [f.id, f]));
const dt = (t, v) => (v ? new Date(v).toLocaleString(t.locale, { dateStyle: 'short', timeStyle: 'short' }) : '-');

function head(doc, title, sub) {
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 22, 'F');
  doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text(clean(title), 12, 10);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(clean(sub), 12, 17);
  doc.setTextColor(20);
}

// vector sketch: whole network grey, shut-off area orange, valves to close red (numbered), houses green
function sketch(doc, project, sh, x0, y0, w, h) {
  const F = fx(project), pts = sh.edges.flat();
  let [la0, la1, lo0, lo1] = [Math.min(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[1]))];
  const k = Math.cos((la0 * Math.PI) / 180), m = Math.max(la1 - la0, (lo1 - lo0) * k, 0.0003) * 0.25;
  la0 -= m; la1 += m; lo0 -= m / k; lo1 += m / k;
  const s = Math.min(w / ((lo1 - lo0) * k), h / (la1 - la0)), cx = x0 + w / 2, cy = y0 + h / 2, mla = (la0 + la1) / 2, mlo = (lo0 + lo1) / 2;
  const P = (p) => [cx + (p[1] - mlo) * k * s, cy - (p[0] - mla) * s];
  const inBox = ([x, y]) => x >= x0 && x <= x0 + w && y >= y0 && y <= y0 + h;
  doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3); doc.rect(x0, y0, w, h);
  doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.6);
  (project.pipes || []).forEach((pp) => {
    const L = pp.points.map((q) => resolvePt(q, project.fittings || [])).filter(Boolean).map(P);
    for (let i = 1; i < L.length; i++) if (inBox(L[i - 1]) && inBox(L[i])) doc.line(...L[i - 1], ...L[i]);
  });
  doc.setDrawColor(249, 115, 22); doc.setLineWidth(1.8);
  sh.edges.forEach(([a, b]) => doc.line(...P(a), ...P(b)));
  sh.houses.forEach((id) => { if (!F[id]) return; doc.setFillColor(21, 128, 61); doc.circle(...P([F[id].lat, F[id].lon]), 1.4, 'F'); });
  doc.setFontSize(7); doc.setFont('helvetica', 'bold');
  sh.valves.forEach((id, i) => { if (!F[id]) return; const [x, y] = P([F[id].lat, F[id].lon]); doc.setFillColor(220, 38, 38); doc.circle(x, y, 2.6, 'F'); doc.setTextColor(255); doc.text(String(i + 1), x, y + 0.9, { align: 'center' }); });
  const o = F[sh.valveId];
  if (o) { const [x, y] = P([o.lat, o.lon]); doc.setFillColor(17, 24, 39); doc.circle(x, y, 2.6, 'F'); doc.setTextColor(255); doc.text('0', x, y + 0.9, { align: 'center' }); }
  doc.setTextColor(20); doc.setFont('helvetica', 'normal');
}

// crew report: map sketch, valves to close, affected households / hydrants
export function shutPdf(t, project, sh) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }), F = fx(project), o = F[sh.valveId];
  head(doc, `${t.shTitle}${project.title ? ` - ${project.title}` : ''}`, `${APP_INFO.name} · ${project.address || ''} · ${new Date().toLocaleString(t.locale)}`);
  doc.setFontSize(10.5); doc.setFont('helvetica', 'bold');
  doc.text(clean(`${t.shValve}: ${o?.name || '-'}   ·   ${t.shLen}: ${sh.len.toFixed(1).replace('.', ',')} m`), 12, 30);
  sketch(doc, project, sh, 12, 34, 186, 105);
  let y = 147;
  const line = (txt, bold, rgb) => {
    if (y > 284) { doc.addPage(); y = 16; }
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(bold ? 10.5 : 9.2); doc.setTextColor(...(rgb || [20, 20, 20]));
    doc.text(clean(txt), 12, y, { maxWidth: 186 }); y += bold ? 6 : 5;
  };
  const ll = (f) => `${f.lat.toFixed(6)}, ${f.lon.toFixed(6)}`;
  line(`${t.shClose} (${sh.valves.length})`, true, [220, 38, 38]);
  sh.valves.forEach((id, i) => { const f = F[id]; if (f) line(`${i + 1}.  ${f.name || ''}${f.sym ? ` [${f.sym}]` : ''}${f.dn || f.hostDn ? ` · ${f.dn || f.hostDn}` : ''} · ${ll(f)}   [ ] ${t.shClosedChk}`); });
  if (sh.opened?.length) line(`${t.shOpenedL}: ${sh.opened.map((id) => F[id]?.name || '').join(', ')}`, false, [180, 83, 9]);
  y += 2;
  line(`${t.shHouses} (${sh.houses.length})`, true, [21, 128, 61]);
  sh.houses.forEach((id) => { const f = F[id]; if (f) line(`•  ${f.name || ''}${f.phone ? ` · ${f.phone}` : ''}${f.email ? ` · ${f.email}` : ''}${f.note ? ` · ${f.note}` : ''}`); });
  if (!sh.houses.length) line(t.shNone);
  y += 2;
  line(`${t.shHyd} (${sh.hydrants.length})`, true);
  line(sh.hydrants.map((id) => F[id]?.name || '').join(', ') || t.shNone);
  if (sh.open) { y += 2; line(`! ${t.shOpen}`, false, [180, 83, 9]); }
  doc.setFontSize(7); doc.setTextColor(100); doc.text(clean(`${APP_INFO.name} · ${t.shPdfFoot}`), 12, 290);
  doc.save(`${safeName(`Absperrbericht ${o?.name || ''}`)}.pdf`);
}

export const noticeText = (t, n) => [t.ntHead, '', `${t.ntFrom}: ${dt(t, n.from)}`, `${t.ntTo}: ${dt(t, n.to)}`, `${t.ntReason}: ${n.reason || '-'}`, '', t.ntBody, '', `${t.ntContact}: ${[n.contact, n.phone].filter(Boolean).join(' · ') || '-'}`].join('\n');

// printable notice for residents (one A4 page)
export function noticePdf(t, project, sh, n) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }), F = fx(project);
  doc.setFillColor(249, 115, 22); doc.rect(0, 0, 210, 34, 'F');
  doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.text(clean(t.ntHead), 105, 21, { align: 'center', maxWidth: 190 });
  doc.setTextColor(20); doc.setFontSize(14);
  let y = 52;
  [[t.ntFrom, dt(t, n.from)], [t.ntTo, dt(t, n.to)], [t.ntReason, n.reason || '-']].forEach(([k, v]) => { doc.setFont('helvetica', 'bold'); doc.text(clean(`${k}:`), 20, y); doc.setFont('helvetica', 'normal'); doc.text(clean(v), 70, y, { maxWidth: 120 }); y += 11; });
  y += 4; doc.setFontSize(12);
  const body = doc.splitTextToSize(clean(t.ntBody), 170);
  doc.text(body, 20, y); y += body.length * 6 + 8;
  doc.setFont('helvetica', 'bold'); doc.text(clean(`${t.ntContact}:`), 20, y); doc.setFont('helvetica', 'normal');
  doc.text(clean([n.contact, n.phone].filter(Boolean).join(' · ') || '-'), 70, y); y += 14;
  const names = sh.houses.map((id) => F[id]?.name).filter(Boolean);
  if (names.length) {
    doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.text(clean(t.ntAffected), 20, y); y += 5; doc.setFont('helvetica', 'normal');
    doc.text(doc.splitTextToSize(clean(names.join(' · ')), 170), 20, y);
  }
  doc.setFontSize(8); doc.setTextColor(100); doc.text(clean(`${project.title || ''} · ${APP_INFO.name}`), 105, 287, { align: 'center' });
  doc.save(`${safeName(`Aushang ${project.title || ''}`)}.pdf`);
}
